<template v-if="partnerInfo">
  <card-container
    :id="pageId"
    :class="pageId"
    :loading-message="status.spinnerMessage"
    :is-loading="status.isLoading"
    button-location="top"
  >
    <template #title>
      Communication Preferences
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <div class="col-sm-5 col-12 form-submit-button-group left-button-group">
          <p-button
            type="info"
            round
            @click="updatePartnerOptions"
          >
            Save Options
          </p-button>
        </div>
      </div>
    </template>

    <template #content>
      <h6 class="sub-section-title">
        Functional Settings
      </h6>

      <div class="row mb-30">
        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isScheduledRideRestricted"
            v-model="isScheduledRideRestricted"
          >
            Restrict Scheduled Rides
          </el-checkbox>
        </div>
      </div>

      <call-options
        v-if="callOptions"
        v-model="callOptions"
        class="call-options"
      />

      <sms-options
        v-if="smsOptions"
        v-model="smsOptions"
        :show-next-ride-sms="false"
        class="sms-options"
      />

      <email-options
        v-if="emailOptions"
        v-model="emailOptions"
        class="email-options"
      />

      <mail-options
        v-if="mailOptions"
        v-model="mailOptions"
        class="mail-options"
      />
    </template>
  </card-container>
</template>

<script lang="ts">
import { ref, Ref, reactive, onMounted } from 'vue';
import { usePartner } from '@/use/partner';

import { CommunicationPreferenceService } from '@/Utils/communicationPreferenceService';

import CallOptions from '@/components/Main/Operator/CallSmsEmailMailOptions/CallOptions.vue';
import SmsOptions from '@/components/Main/Operator/CallSmsEmailMailOptions/SmsOptions.vue';
import EmailOptions from '@/components/Main/Operator/CallSmsEmailMailOptions/EmailOptions.vue';
import MailOptions from '@/components/Main/Operator/CallSmsEmailMailOptions/MailOptions.vue';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { fetchPartner } from '@/services/partners/fetchPartner';
import { updatePartner } from '@/services/partners/updatePartner';

const pageId = 'partner-call-sms-email-mail-options';

export default {
  name: 'PartnerCallSmsEmailMailOptions',

  components: {
    CallOptions,
    SmsOptions,
    EmailOptions,
    MailOptions,
  },

  setup: () => {
    const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();

    const { partnerInfo } = usePartner();

    const callOptions: Ref<any> = ref({});
    const smsOptions: Ref<any> = ref({});
    const emailOptions: Ref<any> = ref({});
    const mailOptions: Ref<any> = ref({});
    const isScheduledRideRestricted = ref(false);

    const communicationPreferenceService = new CommunicationPreferenceService();

    const status = reactive({
      isLoading: false,
      spinnerMessage: '',
    });

    const fillOptionsWithPartnerInfo = (partnerInfo: any) => {
      callOptions.value = communicationPreferenceService.getCallOptionsKV(partnerInfo.callOptions);
      smsOptions.value = communicationPreferenceService.getSmsOptionsKV(partnerInfo.smsOptions);
      emailOptions.value = communicationPreferenceService.getEmailOptionsKV(partnerInfo.emailOptions);
      mailOptions.value = communicationPreferenceService.getMailOptionsKV(partnerInfo.mailOptions);
      isScheduledRideRestricted.value = Boolean(partnerInfo.isScheduledRideRestricted);
    };

    const fetchPartnerOptions = async () => {
      resetForm();

      status.spinnerMessage = 'Loading Options...';
      status.isLoading = true;

      try {
        const response = await fetchPartner({
          codeName: partnerInfo.value.codeName,
        });

        fillOptionsWithPartnerInfo(response);
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isLoading = false;
      }
    };

    const updatePartnerOptions = async () => {
      const options = communicationPreferenceService.getUpdateDataOptionsKV(
        {
          callOptions: callOptions.value,
          smsOptions: smsOptions.value,
          emailOptions: emailOptions.value,
          mailOptions: mailOptions.value,
        },
        false,
      );

      const isUpdateCommunicationPreference = await showConfirmPopupDialog({
        text: 'Be advised that this action will also apply changes to the communication preferences for all associated members under this partner. Would you like to continue with this update?',
        title: 'Update Communication Preferences?',
      });

      if (!isUpdateCommunicationPreference) {
        return;
      }

      status.spinnerMessage = 'Updating Partner Options...';
      status.isLoading = true;
      try {
        await updatePartner({
          codeName: partnerInfo.value.codeName,
          data: {
            ...options,
            isScheduledRideRestricted: isScheduledRideRestricted.value,
          },
        });

        showSuccessPopupDialog({
          text: 'The partner options have been updated successfully.',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      } finally {
        status.isLoading = false;
      }
    };

    const resetForm = () => {
      callOptions.value = {};
      smsOptions.value = {};
      emailOptions.value = {};
      mailOptions.value = {};
      isScheduledRideRestricted.value = false;
    };

    onMounted(() => {
      if (partnerInfo.value) {
        fetchPartnerOptions();
      }
    });

    return {
      pageId,
      status,
      partnerInfo,

      callOptions,
      smsOptions,
      emailOptions,
      mailOptions,
      isScheduledRideRestricted,

      updatePartnerOptions,
      fetchPartnerOptions,
    };
  },
};
</script>

<style lang="scss">
.user-call-sms-email-mail-options {
  .help-block {
    line-height: 15px;
    color: #968b8b;
    padding-left: 35px;
    padding-top: 0;
  }
}
</style>
